import {
  LogEntry,
  LoggerConfig,
  LoggerData,
  LoggerMethods,
  LoggerStyles,
  LogLevel,
} from '../types/utils/logger.types';

// ----------------------------------------
// Extended interfaces
// ----------------------------------------
interface ExtendedLoggerStyles extends LoggerStyles {
  rerender: string;
  debug: string;
}

interface ExtendedLoggerMethods extends LoggerMethods {
  rerender: (
    componentName: string,
    data?: LoggerData & {
      renderCount?: number;
      reason?: string;
      props?: Record<string, unknown>;
      deps?: Record<string, unknown>;
    }
  ) => void;
  debug: (message: string, data?: LoggerData) => void;
}

// ----------------------------------------
// Defaults
// ----------------------------------------
const DEFAULT_STYLES: ExtendedLoggerStyles = {
  state:
    'background: #2196F3; color: white; padding: 2px 5px; border-radius: 2px;',
  update:
    'background: #4CAF50; color: white; padding: 2px 5px; border-radius: 2px;',
  warning:
    'background: #FFC107; color: black; padding: 2px 5px; border-radius: 2px;',
  error:
    'background: #F44336; color: white; padding: 2px 5px; border-radius: 2px;',
  trace: 'color: #666; font-style: italic;',
  rerender:
    'background: #9C27B0; color: white; padding: 2px 5px; border-radius: 2px;',
  debug:
    'background: #607D8B; color: white; padding: 2px 5px; border-radius: 2px;',
};

const DEFAULT_CONFIG: LoggerConfig = {
  prefix: 'Logger',
  minLevel: 'debug',
  enableStackTrace: true,
  enabled: false,
};

export const createLogger = (
  configOrPrefix: LoggerConfig | string
): ExtendedLoggerMethods => {
  // If the caller just provides a string, treat that as the prefix. Otherwise, merge objects.
  const config: LoggerConfig =
    typeof configOrPrefix === 'string'
      ? { ...DEFAULT_CONFIG, prefix: configOrPrefix }
      : { ...DEFAULT_CONFIG, ...configOrPrefix };

  const styles: ExtendedLoggerStyles = {
    ...DEFAULT_STYLES,
    ...(config.customStyles || {}),
  };

  let currentLevel: LogLevel = config.minLevel || 'debug';
  let enableTrace: boolean = config.enableStackTrace ?? true;
  // Our new “enabled” boolean to control all logs
  let isEnabled: boolean = config.enabled !== false;

  let entries: LogEntry[] = [];
  const renderCounts: Record<string, number> = {};

  const levels: LogLevel[] = ['debug', 'info', 'warning', 'error'];

  const shouldLog = (level: LogLevel): boolean => {
    // First check if globally enabled
    if (!isEnabled) return false;

    // Next see if the current log level is at or above minLevel
    return levels.indexOf(level) >= levels.indexOf(currentLevel);
  };

  const addLogEntry = (
    level: LogLevel,
    message: string,
    data?: LoggerData
  ): void => {
    entries.push({
      timestamp: new Date(),
      level,
      message,
      data,
      stackTrace: enableTrace ? new Error().stack : undefined,
    });
  };

  const logWithLevel = (
    level: LogLevel,
    style: keyof ExtendedLoggerStyles,
    emoji: string,
    message: string,
    data?: LoggerData
  ): void => {
    if (!shouldLog(level)) return;

    addLogEntry(level, message, data);

    console.groupCollapsed(
      `%c[${config.prefix}] ${emoji} ${message}`,
      styles[style]
    );

    if (data !== undefined && data !== null) {
      console.log('Data:', data);
    }

    if (enableTrace) {
      console.trace('Stack trace:');
    }

    console.groupEnd();
  };

  // -------------------------------------------------------
  // ExtendedLoggerMethods
  // -------------------------------------------------------
  const logger: ExtendedLoggerMethods = {
    state: (message: string, data?: LoggerData): void => {
      logWithLevel('info', 'state', '📊', message, data);
    },
    update: (message: string, data?: LoggerData): void => {
      logWithLevel('info', 'update', '🔄', message, data);
    },
    warning: (message: string, data?: LoggerData): void => {
      logWithLevel('warning', 'warning', '⚠️', message, data);
    },
    error: (message: string, data?: LoggerData): void => {
      logWithLevel('error', 'error', '🚨', message, data);
    },
    debug: (message: string, data?: LoggerData): void => {
      logWithLevel('debug', 'debug', '🔍', message, data);
    },
    rerender: (
      componentName: string,
      data?: LoggerData & {
        renderCount?: number;
        reason?: string;
        props?: Record<string, unknown>;
        deps?: Record<string, unknown>;
      }
    ): void => {
      renderCounts[componentName] = (renderCounts[componentName] || 0) + 1;

      const renderData = {
        renderCount: renderCounts[componentName],
        timestamp: new Date().toISOString(),
        ...data,
      };

      logWithLevel(
        'info',
        'rerender',
        '🔄',
        `Rerender: ${componentName} (${renderCounts[componentName]})`,
        renderData
      );
    },

    // -------------------------------------------------------
    // Additional methods
    // -------------------------------------------------------
    setLevel: (level: LogLevel): void => {
      currentLevel = level;
    },

    getEntries: (): LogEntry[] => {
      return [...entries];
    },

    clearEntries: (): void => {
      entries = [];
    },

    enableStackTrace: (enable: boolean): void => {
      enableTrace = enable;
    },

    // A new convenience method to turn logging on and off
    setEnabled: (enable: boolean): void => {
      isEnabled = enable;
    },
  };

  return logger;
};

// A type guard for validating logger data
export const isValidLoggerData = (data: unknown): data is LoggerData => {
  return (
    data === null ||
    data === undefined ||
    (typeof data === 'object' && !Array.isArray(data))
  );
};
